import type { ValidationField } from "./types/consoleTypes";

export type ValidationDisplayRow = {
  key: string;
  depth: number;
  parameter: string;
  nodeName: string; // The "Human-Friendly" parameter label
  nodeType: "subsystem" | "channel" | "sensor"; // Satellite domain iconography
  fullPath: string; // Absolute Telemetry/Command path
  hasChildren: boolean;
  isLastChild: boolean;
  ancestorRails: boolean[]; // True if a vertical line should be drawn at this level's index
  parentKey?: string;
  validations?: string;
  resultValue?: string;
  resultStatus?: string;
  hasChildFailure?: boolean;
  statusSummary?: string; // e.g., "✖ 2 Fails" (Anomalies)
  valueType?: "number" | "boolean" | "string" | "null" | "array" | "object";
};

/** Backend often sends array items as Parameter "0", "1"; show as parent[0]. */
function isArrayIndexParameter(name: string): boolean {
  return /^\d+$/.test(String(name).trim());
}

/**
 * Merges sibling validation nodes that share the exact same parameter (e.g., when the backend
 * returns multiple nodes for array index "7" containing separate fields like Stats_names and Stats_Counter).
 */
function mergeSiblingValidationNodes(nodes: ValidationField[]): ValidationField[] {
  const merged: ValidationField[] = [];
  const map = new Map<string, ValidationField>();

  for (const node of nodes) {
    const paramKey = String(node.Parameter ?? "").trim();
    if (paramKey && map.has(paramKey)) {
      const existing = map.get(paramKey)!;
      const existingChildren = existing.children ?? [];
      const newChildren = node.children ?? [];
      existing.children = [...existingChildren, ...newChildren];

      const existingStatus = String(existing.ResultStatus ?? "").toLowerCase().trim();
      const newStatus = String(node.ResultStatus ?? "").toLowerCase().trim();
      if (newStatus === "fail" || existingStatus === "fail") {
        existing.ResultStatus = "Fail";
      } else if (newStatus === "timeout" || existingStatus === "timeout") {
        existing.ResultStatus = "Timeout";
      } else if (!existing.ResultStatus && node.ResultStatus) {
        existing.ResultStatus = node.ResultStatus;
      }

      if (!existing.Validations && node.Validations) {
        existing.Validations = node.Validations;
      }
      if ((existing.ResultValue == null || existing.ResultValue === "—") && node.ResultValue) {
        existing.ResultValue = node.ResultValue;
      }
    } else {
      const copy: ValidationField = {
        ...node,
        children: node.children ? [...node.children] : undefined,
      };
      if (paramKey) {
        map.set(paramKey, copy);
      }
      merged.push(copy);
    }
  }

  for (const node of merged) {
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children = mergeSiblingValidationNodes(node.children);
    }
  }

  return merged;
}

function getStatusCounts(node: ValidationField): { pass: number; fail: number; timeout: number } {
  let pass = 0;
  let fail = 0;
  let timeout = 0;

  const s = String(node.ResultStatus ?? "").toLowerCase().trim();
  if (s === "pass") pass++;
  else if (s === "fail") fail++;
  else if (s === "timeout" || s === "timed_out" || s === "timedout") timeout++;

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      const counts = getStatusCounts(child);
      pass += counts.pass;
      fail += counts.fail;
      timeout += counts.timeout;
    });
  }

  return { pass, fail, timeout };
}

export function flattenValidationRows(
  rawNodes: ValidationField[],
  depth = 0,
  parentKey = "root",
  ancestorRails: boolean[] = [],
  parentPath = "",
): ValidationDisplayRow[] {
  const nodes = depth === 0 ? mergeSiblingValidationNodes(rawNodes) : rawNodes;

  return nodes.reduce<ValidationDisplayRow[]>((acc, node, index) => {
    const rawParam = node.Parameter ?? "—";

    const key = `${parentKey}-${index}-${rawParam}`;
    const children = Array.isArray(node.children) ? node.children : [];
    const hasChildren = children.length > 0;
    const isLastChild = index === nodes.length - 1;

    const isArrayIndex = isArrayIndexParameter(rawParam);
    const isWildcardArray =
      rawParam.includes("[*]") ||
      rawParam === "*" ||
      node.type === "allElementsInArraySatisfy";

    let nodeName = rawParam;
    if (isArrayIndex) {
      const idx = Number(rawParam);
      nodeName = `#${idx + 1} [${rawParam}]`;
    } else if (isWildcardArray) {
      const cleanParam = rawParam.replace(/\[\*\]$/, "").trim();
      nodeName = cleanParam ? `${cleanParam} [*]` : "[*] All Elements";
    }

    let nodeType: ValidationDisplayRow["nodeType"] = "sensor";
    if (hasChildren) {
      const isArray =
        node.type?.toLowerCase().trim() === "array" ||
        isWildcardArray ||
        isArrayIndex;
      nodeType = isArray ? "channel" : "subsystem";

      // Append (X items) ONLY for array collections (not individual element objects like #8 [7])
      if (
        (node.type?.toLowerCase().trim() === "array" || isWildcardArray) &&
        !isArrayIndex
      ) {
        nodeName = `${nodeName} (${children.length} ${
          children.length === 1 ? "item" : "items"
        })`;
      }
    }

    // Build the full path for tooltips
    const fullPath =
      parentPath === ""
        ? rawParam
        : isArrayIndex
        ? `${parentPath}[${rawParam}]`
        : `${parentPath}.${rawParam}`;

    let statusSummary: string | undefined;
    let hasChildFailure = false;
    if (hasChildren) {
      const { pass, fail, timeout } = getStatusCounts(node);
      if (fail > 0 && timeout > 0) {
        statusSummary = `✖ ${fail} ${fail === 1 ? "Fail" : "Fails"}, ⏱ ${timeout} Timeout`;
        hasChildFailure = true;
      } else if (fail > 0) {
        statusSummary = `✖ ${fail} ${fail === 1 ? "Fail" : "Fails"}`;
        hasChildFailure = true;
      } else if (timeout > 0) {
        statusSummary = `⏱ ${timeout} Timeout`;
        hasChildFailure = true;
      }
    } else {
      const s = String(node.ResultStatus ?? "").toLowerCase().trim();
      hasChildFailure = s === "fail" || s === "timeout" || s === "timed_out" || s === "timedout";
    }

    let valueType: ValidationDisplayRow["valueType"] = "string";
    if (hasChildren) {
      valueType = node.type?.toLowerCase().trim() === "array" ? "array" : "object";
    } else {
      const v = String(node.ResultValue ?? "").trim();
      if (v === "" || v === "—") valueType = "null";
      else if (v.toLowerCase() === "true" || v.toLowerCase() === "false") valueType = "boolean";
      else if (!isNaN(Number(v)) && !v.includes(" ") && v !== "") valueType = "number";
    }

    acc.push({
      key,
      depth,
      hasChildren,
      isLastChild,
      ancestorRails: [...ancestorRails],
      parentKey: parentKey !== "root" ? parentKey : undefined,
      parameter: rawParam,
      nodeName,
      nodeType,
      fullPath,
      validations: node.Validations ?? undefined,
      resultValue: node.ResultValue ?? undefined,
      resultStatus: node.ResultStatus ?? undefined,
      hasChildFailure,
      statusSummary,
      valueType,
    });

    if (hasChildren) {
      // For children, add a rail at the current depth ONLY if this node has siblings below it
      const nextAncestorRails = [...ancestorRails];
      nextAncestorRails[depth] = !isLastChild;

      acc.push(
        ...flattenValidationRows(
          children,
          depth + 1,
          key,
          nextAncestorRails,
          fullPath,
        ),
      );
    }

    return acc;
  }, []);
}
